import { useCallback, useMemo } from 'react';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { StackedTextCell } from '@retail/components';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid-premium';
import { PricePerUnit } from '@retail/products/components';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { getLLDate } from '@retail/utils';
import { dataGridBoldClass } from '@shared/styles';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import { PriceTypeTooltip } from '../PriceResultType';

export interface RowPriceProps {
  includeVat: boolean;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 130,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export const useItemPriceColumns = ({ includeVat }: RowPriceProps) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const getFormatPrice = useCallback(
    (price?: number) => (price ? formatPrice(price, i18n.language) : '-'),
    [i18n.language]
  );

  return useMemo(() => {
    const purchasePriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'purchasePrice',
      headerName: t('myAssortment.columns.purchasePrice.price'),
      renderCell: ({ row }) => {
        const purchasePrice = getFormatPrice(row.pricesExVat.purchasePrice);
        const priceDate = getLLDate(row.purchasePriceDate);

        return (
          <StackedTextCell
            nullableBottomLine
            topLine={<PricePerUnit price={purchasePrice} unit={row.primaryUnit} />}
            bottomLine={priceDate}
          />
        );
      },
      valueGetter: (_, row) => row.pricesExVat.purchasePrice,
    };
    const costPriceInSalesUnitColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'costPriceInSalesUnit',
      width: 160,
      headerName: t('myAssortment.columns.costPrice.price'),
      renderCell: ({ row }) => {
        const priceDate = getLLDate(row.costPriceDate);

        return (
          <StackedTextCell
            nullableBottomLine
            topLine={
              <PricePerUnit
                price={row.pricesExVat.costPrice ?? '-'}
                unit={row.primaryUnit}
                PriceTooltipContent={
                  <Stack flexDirection="row" gap={1} alignItems="center">
                    <Typography variant="body2">
                      {t('assortment.vat.equivalentTo')}{' '}
                      {t('assortment.vat.included').toLowerCase()}:
                    </Typography>

                    <Typography variant="subtitle2">
                      {t('products.prices.price', {
                        price: getFormatPrice(row.pricesIncVat.costPrice),
                      })}
                    </Typography>
                  </Stack>
                }
              />
            }
            bottomLine={priceDate}
          />
        );
      },
      valueGetter: (_, row) => row.pricesIncVat.costPrice,
    };
    const costPriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'costPrice',
      width: 160,

      headerName: t('myAssortment.columns.costPrice.priceBPU'),
      renderCell: ({ row }) => {
        const priceDate = getLLDate(row.costPriceDate);
        const costPrice = row.pricesExVat.costPriceBasePriceUnit;
        return (
          <StackedTextCell
            nullableBottomLine
            topLine={costPrice ? <PricePerUnit price={costPrice} unit={row.basePriceUnit} /> : '-'}
            bottomLine={priceDate}
          />
        );
      },
      valueGetter: (_, row) => row.pricesIncVat.costPriceBasePriceUnit,
    };
    const salesPriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'salesPrice',
      headerName: t('myAssortment.columns.salesPrice'),
      width: 220,
      renderHeader: () => (
        <Typography variant="subtitle1">
          {includeVat
            ? t('myAssortment.columns.salesPriceIncVat')
            : t('myAssortment.columns.salesPriceExVat')}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const price = includeVat ? row.pricesIncVat.salesPrice : row.pricesExVat.salesPrice;
        const validFrom = getLLDate(row.salesPriceDate);

        return (
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <StackedTextCell
              topLine={<PricePerUnit price={price ? price : '-'} unit={row.primaryUnit} />}
              nullableBottomLine
              bottomLine={validFrom}
            />
            {row.hasAppliedRoundingCondition() && (
              <Tooltip title={t('myAssortment.columns.rounding.isRounded')}>
                <CurrencyExchangeOutlinedIcon
                  fontSize="small"
                  aria-label={t('myAssortment.columns.rounding.isRounded')}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
      valueGetter: (_, row) => row.pricesIncVat.salesPrice,
    };

    const salesPriceCoverageColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 120,
      field: 'salesPriceCoverage',
      headerName: t('myAssortment.columns.salesCoverage'),
      renderCell: ({ row }) => {
        const salesPriceCoverage = row.coverageDegree;

        return salesPriceCoverage
          ? `${formatPrice(Number(salesPriceCoverage.toFixed(2)), i18n.language)}%`
          : '-';
      },
      valueGetter: (_, row) => row.coverageDegree,
    };

    const futurePurchasePriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'futurePurchasePrice',
      headerName: t('myAssortment.columns.futurePurchasePrice.price'),
      width: 170,
      renderCell: ({ row }) => {
        const futurePrice = row.pricesExVat.futurePurchasePrice;
        return (
          <StackedTextCell
            nullableBottomLine
            topLine={
              futurePrice ? <PricePerUnit price={futurePrice} unit={row.primaryUnit} /> : '-'
            }
            bottomLine={getLLDate(row.futurePurchasePriceDate)}
          />
        );
      },
      valueGetter: (_, row) => row.pricesExVat.futurePurchasePrice,
    };
    const futureSalesPriceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'futureSalesPrice',
      headerName: t('myAssortment.columns.futurePrice'),
      width: 150,
      renderCell: ({ row }) => {
        const salesPrice = includeVat
          ? row.pricesIncVat.futureSalesPrice
          : row.pricesExVat.futureSalesPrice;

        const priceIsRounded = !!row.futureRoundingCondition;

        return (
          <Box display="flex" gap={1}>
            <StackedTextCell
              topLine={
                salesPrice ? <PricePerUnit price={salesPrice} unit={row.primaryUnit} /> : '-'
              }
              bottomLine={getLLDate(row.futureSalesPriceDate)}
              nullableBottomLine
            />
            {priceIsRounded && (
              <Tooltip title={t('myAssortment.columns.rounding.isRounded')}>
                <CurrencyExchangeOutlinedIcon
                  fontSize="small"
                  aria-label={t('myAssortment.columns.rounding.isRounded')}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
      valueGetter: (_, row) => row.pricesIncVat.futureSalesPrice,
    };
    const priceSourceColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      width: 120,
      headerAlign: 'center',
      align: 'center',
      field: 'priceResultType',
      headerName: t('myAssortment.columns.originatesFrom'),
      renderCell: ({ row }) => {
        return row.markupOrPriceSuggestionCondition ? (
          <PriceTypeTooltip
            currentCondition={row.markupOrPriceSuggestionCondition}
            currentRoundingCondition={row.hasAppliedRoundingCondition() ? row.roundingCondition : undefined}
            futureCondition={row.futureMarkupOrPriceSuggestionCondition}
            futureRoundingCondition={row.futureRoundingCondition}
          />
        ) : (
          '-'
        );
      },
      valueGetter: (_, row) => {
        const markupOrPriceSuggestionCondition = row.markupOrPriceSuggestionCondition;
        return markupOrPriceSuggestionCondition?.type;
      },
    };

    return {
      purchasePriceColumn,
      costPriceInSalesUnitColumn,
      costPriceColumn,
      salesPriceColumn,
      salesPriceCoverageColumn,
      futurePurchasePriceColumn,
      futureSalesPriceColumn,
      priceSourceColumn,
    };
  }, [t, getFormatPrice, includeVat, i18n.language]);
};
